/**
 * ListActions — turn a set of customers into a list you can actually use.
 *
 * Taylor, 2026-09-08: "I wonder if with some of the customer directy filters we
 * jsut add a button that is like create contct list from this get travelers from
 * list and these buttons possibly others then just generates donwloads" — and,
 * from Account Health, "we should be able to create lsits like email our
 * sturggling customers and get that list."
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * EVERY CONTACT LIST GOES THROUGH /api/contacts. THAT IS THE WHOLE DESIGN.
 *
 * The Segments page used to build its email CSV IN THE BROWSER, off
 * /api/customers, from the `key_contact_email` column:
 *
 *     lines.push([csv(r.key_contact_email), csv(r.name), …].join(','));
 *     a.download = `segment-email-….csv`; a.click();
 *
 * /api/customers has no consent model at all. So that download included people
 * who had opted out, whose address had bounced, who had left the company, and
 * whose record HubSpot had merged away — and nothing recorded that the pull ever
 * happened. Meanwhile the Contacts page did it correctly, through
 * api/src/lib/contact-list, which excludes all four by default, returns the
 * count it excluded so the operator can see it, and writes a
 * crm.contact_export_log row for every read, JSON and CSV alike.
 *
 * Taylor's read was that Contacts duplicated Segments. It did overlap — but the
 * page that was doing it RIGHT was the one being cut. So retiring Contacts meant
 * moving its callers onto the same library, not dropping the library: these
 * buttons hit /api/contacts, and suppression plus the audit come along for free.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHY THE SERVER MUST BUILD THE FILE
 *
 * A browser-built CSV cannot be audited — there is no request to log — and it
 * can only contain columns the page already fetched, which is what made
 * `key_contact_email` (one contact per account) look like a contact list in the
 * first place. Asking the server means the real Contact table, the real consent
 * rules, and one audit row per pull.
 */
import { useState } from 'react';
import { acquireToken } from '@/auth/msal';
import { crmUrl, getCrmScopes } from '@/features/crm/crm-config';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';

export interface ListActionsProps {
  /** The customers the current filter selected. */
  customerIds: string[];
  /** Shown on the buttons for context, e.g. "Critical accounts". */
  label?: string;
  /** Rendered when there is nothing selected. */
  disabledReason?: string;
}

interface Preview {
  rowCount: number;
  suppressedExcluded: number;
}

/** Above this we do not ask the server to inline the whole list first. */
const PREVIEW_CEILING = 5000;

export function ListActions({
  customerIds,
  label,
  disabledReason,
}: ListActionsProps) {
  const [busy, setBusy] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [includeSuppressed, setIncludeSuppressed] = useState(false);

  const empty = customerIds.length === 0;

  /**
   * Build the query. `customerIds` is repeated rather than comma-joined for
   * long sets — the parser accepts both, and a 2,000-id comma string is a URL
   * length gamble on some proxies.
   */
  const params = (extra: Record<string, string> = {}) => {
    const sp = new URLSearchParams();
    // The lib's parser splits on commas, so chunking keeps each value short
    // without needing a different server contract.
    for (let i = 0; i < customerIds.length; i += 50) {
      sp.append('customerIds', customerIds.slice(i, i + 50).join(','));
    }
    sp.set('hasEmail', '1');
    if (includeSuppressed) sp.set('includeSuppressed', '1');
    for (const [k, v] of Object.entries(extra)) sp.set(k, v);
    return sp;
  };

  const countIt = async () => {
    setBusy('count');
    setError(null);
    try {
      const sp = params({ limit: '1' });
      const d = await apiFetch<{
        total?: number;
        rowCount?: number;
        suppressedExcluded: number;
      }>(`/api/contacts?${sp.toString()}`);
      setPreview({
        rowCount: d.total ?? d.rowCount ?? 0,
        suppressedExcluded: d.suppressedExcluded ?? 0,
      });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  /**
   * The download.
   *
   * A plain link with the session cookie would be simpler, but this API is
   * called with a bearer token (see lib/api-fetch + crm-config), so the file has
   * to be fetched and handed to the browser as a blob.
   */
  const download = async () => {
    setBusy('csv');
    setError(null);
    try {
      const sp = params({ format: 'csv' });
      /**
       * The CRM-audience token, minted through the scope-parameterized
       * acquireToken(getCrmScopes()) — NOT the parameterless one, which mints
       * for the experts audience and would 401 here (see crm-config.ts).
       */
      const token = await acquireToken(getCrmScopes());
      const res = await fetch(crmUrl(`/api/contacts?${sp.toString()}`), {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) {
        throw new Error(
          `Export failed (${res.status}). ${await res.text().catch(() => '')}`.trim(),
        );
      }
      // The server reports both on headers so the UI can be honest about what
      // the file contains without parsing it.
      setPreview({
        rowCount: Number(res.headers.get('X-Export-Row-Count') ?? 0),
        suppressedExcluded: Number(
          res.headers.get('X-Export-Suppressed-Excluded') ?? 0,
        ),
      });
      // The server names the file from the filters it actually applied, so
      // honour Content-Disposition rather than inventing a name that could
      // disagree with the contents.
      const disposition = res.headers.get('Content-Disposition') ?? '';
      const named = /filename="([^"]+)"/.exec(disposition)?.[1];
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download =
        named ?? `contact-list-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="list-actions" data-testid="list-actions">
      <div className="list-actions-row">
        <button
          className="btn"
          data-testid="list-count"
          disabled={empty || busy !== null}
          onClick={countIt}
          type="button"
        >
          {busy === 'count' ? 'Counting…' : 'Count contacts'}
        </button>
        <button
          className="btn btn-primary"
          data-testid="list-download"
          disabled={
            empty || busy !== null || customerIds.length > PREVIEW_CEILING
          }
          onClick={download}
          type="button"
        >
          {busy === 'csv'
            ? 'Building…'
            : `Create contact list${label ? ` — ${label}` : ''}`}
        </button>
      </div>

      {empty ? (
        <div className="list-actions-note">
          {disabledReason ?? 'Apply a filter to build a list.'}
        </div>
      ) : (
        <div className="list-actions-note">
          {customerIds.length.toLocaleString()} accounts selected. The list is
          built from the Contact records at those accounts, not from the single
          key contact on the customer row.
        </div>
      )}

      {/* CONSENT IS DEFAULT-ON, AND THE OVERRIDE SAYS WHAT IT DOES. Not a
          checkbox called "include all" — the label names the four states being
          overridden, because that is the decision being taken. Every pull is
          recorded server-side with this flag's value. */}
      <label className="list-actions-consent">
        <input
          checked={includeSuppressed}
          data-testid="list-include-suppressed"
          onChange={(e) => setIncludeSuppressed(e.target.checked)}
          type="checkbox"
        />
        <span>
          Include people who have opted out, bounced, left the company, or been
          merged in HubSpot. Off by default; turning it on is recorded against
          this export.
        </span>
      </label>

      {preview ? (
        <div className="list-actions-preview" data-testid="list-preview">
          <strong>{preview.rowCount.toLocaleString()}</strong> contacts
          {preview.suppressedExcluded > 0 ? (
            <>
              {' · '}
              <span className="list-actions-suppressed">
                {preview.suppressedExcluded.toLocaleString()} excluded for
                consent
              </span>
            </>
          ) : null}
        </div>
      ) : null}

      {error ? <div className="banner banner-error">{error}</div> : null}
    </div>
  );
}

export default ListActions;
